import { UNIT_TILE_SIZE_PX } from './sizes';
import type { UnitIcon, UnitTileSize } from './types';

/** The `<img>` attributes derived from a set of icon renditions. */
export interface ResolvedIconSources {
	sizes?: string;
	src?: string;
	srcSet?: string;
}

/**
 * Chooses the `<img>` sources for a set of icon renditions at a given tile size.
 *
 * Emits **width** descriptors (`icon.png 64w`) plus a `sizes` attribute rather
 * than pixel-density descriptors (`icon.png 2x`). Both let the browser upgrade to
 * a crisper rendition on HiDPI displays, but width descriptors mean no density
 * arithmetic is done here at all: the browser divides by its own device pixel
 * ratio. That avoids awkward fractional descriptors (a 32px rendition in a 48px
 * tile is `0.67x`) and keeps the rendered size in one place.
 *
 * This lives in `unit-tile` rather than in each caller because the rendered pixel
 * size the choice depends on is owned here. A caller cannot compute it without
 * duplicating `UNIT_TILE_SIZE_PX`, and a duplicate would silently skew if a tile
 * size ever changed.
 *
 * Returns an empty object when there is no usable rendition, so callers can fall
 * back to a single `iconUrl`.
 */
export function resolveIconSources(
	icons: UnitIcon[] | undefined,
	size: UnitTileSize,
): ResolvedIconSources {
	// Width descriptors are meaningless without a positive width, and renditions are
	// typically server-supplied, so normalise before trusting the payload. Keying by
	// width also drops duplicates, which the browser would ignore anyway.
	const byWidth = new Map<number, string>();

	for (const icon of icons ?? []) {
		const isUsable = Boolean(icon?.url) && Number.isFinite(icon?.width) && icon.width > 0;

		if (isUsable && !byWidth.has(icon.width)) {
			byWidth.set(icon.width, icon.url);
		}
	}

	if (byWidth.size === 0) {
		return {};
	}

	const candidates = [...byWidth.entries()].sort(([a], [b]) => a - b);
	const renderedPx = UNIT_TILE_SIZE_PX[size];

	// `src` is just the fallback for anything that ignores `srcSet`, but it also
	// drives the tile's load/error handling, so point it at the smallest rendition
	// that still covers the tile. If every rendition is smaller, the largest is the
	// closest available.
	const [, fallbackUrl] =
		candidates.find(([width]) => width >= renderedPx) ?? candidates[candidates.length - 1];

	return {
		src: fallbackUrl,
		srcSet: candidates.map(([width, url]) => `${url} ${width}w`).join(', '),
		sizes: `${renderedPx}px`,
	};
}
